import {StateController} from "@lit-app/state/src/state-controller";
import {Router} from "@lit-labs/router";
import {create as lottieInteractivityCreate} from "@lottiefiles/lottie-interactivity";
import {LottiePlayer} from "@lottiefiles/lottie-player";

import {html, HTMLTemplateResult} from "lit";
import {get, translate, use} from "lit-translate";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {createRef, ref} from "lit/directives/ref.js";
import {repeat} from "lit/directives/repeat.js";
import {when} from "lit/directives/when.js";
import {auditTime, fromEvent, map, merge, share, take, takeUntil} from "rxjs";
import {container} from "tsyringe";

import data from "../../../public/logolottie.json";
import {appState} from "../../app.state";
import {PersistenceConfig} from "../../persistence/persistence.config";
import {PersistencePreloader} from "../../persistence/persistence.preloader";
import {PointerPositionHelper} from "../../helpers/pointer-position.helper";
import {AbstractLitElement} from "../abstract-lit.element";
import {ModeRouterController} from "../_reactive-controllers/mode-router.controller";
import "../ha-mode-switch/ha-mode-switch.component";
import "../knowledge/knowledge-sidebar-list/knowledge-sidebar-list.component";
import "../linkview/linkview-sidebar-list/linkview-sidebar-list.component";
import {NavigationItem} from "../ha-navigation-item/navigation-item.interface";
import {haAppStyles} from "./ha-app.styles";
import {routes} from "./routes";
import {pageTitleFor} from "./page-title.helper";
import {FEATURE_FLAGS} from "../../feature-flags";
import {buildNavItems, isNavItemActive, PERSONAL_ORGANIZER_NAV} from "./nav-item-filters";
import {computeDraggedWidth, computeSidebarStyle, computeToggledSidebarWidth, parseKnowledgeSid, parseLinkviewSid} from "./sidebar-resize.helper";
import {awaitAuthHydration} from "../../services/auth-hydration.gate";
import {bindTimerWatchToSession} from "../../services/timer-watch";
import {sanitizeTenant} from "./tenant-context.helper";
import {getSubMenuItems} from "../../toolbar-items/o2d-burger-menu.toolbar-item";
import {UserDisplayNameController} from "./user-display-name.controller";
import "../../views/ha-login.view";

// Module-scoped augmentation for the serve-time injected tenant global (HomeAction.php).
// Kept co-located here so the single-frontend-component scope stays intact.
declare global {
    interface Window {
        __APP_CONTEXT__?: string;
    }

    // Navigation request handled by the router listener below; dispatched on `window`
    // by routes.ts (the issue route's dialog `onClose`).
    interface WindowEventMap {
        'va.router.goto': CustomEvent<{ value: string }>;
    }
}

@customElement(`ha-app`)
export class HaAppComponent extends AbstractLitElement {

    static styles = haAppStyles;

    @property({type: Array})
    public navigation: NavigationItem[] = [];

    /** Guards against rendering before translations are loaded. */
    @state() private _translationsReady = false;

    /**
     * The logged-in user's display name, shown under the "Einstellungen"
     * sidebar button (Anke, 2026-09-18). `appState.user` itself only carries
     * `username`/`uid` — the actual name lives on the linked `Employee`
     * (`appState.employee`, resolved async via `UserRepository`), so this is
     * cached into state rather than re-resolved on every render. Falls back
     * to the bare username when no Employee is linked (or resolution fails)
     * rather than showing nothing, since `appState.user` guarantees at least
     * that much whenever `isUserLoggedIn` is true.
     */
    @state() private _userDisplayName = '';

    private readonly _userDisplayNameController = new UserDisplayNameController({
        isLoggedIn: () => appState.isUserLoggedIn,
        getUsername: () => appState.user?.username ?? null,
        getEmployee: () => appState.employee,
        onName: (name) => { this._userDisplayName = name; },
    });

    /**
     * Determines if sidebar is collapsed based on user preference or responsive default
     */
    private get _isSidebarCollapsed(): boolean {
        // If user has manually set a preference, use it
        if (appState.sidebarNavigationState !== null) {
            return appState.sidebarNavigationState === 'collapsed';
        }
        
        // Otherwise use responsive default
        return window.innerWidth < 768;
    }

    private _lottiePlayerRef = createRef<LottiePlayer>();

    // Backs the sidebar resize drag handle (todo_sidebar.css `.sidebar-resize-handle`,
    // operator request 2026-08-17). Needed to read/set the live element width —
    // Lit's `ref` directive is the supported way to reach the actual DOM node
    // from a mouse-drag handler, which is imperative by nature.
    private _sidebarRef = createRef<HTMLElement>();

    // Sole writer of `appState.currentMode` — derives mode from current URL.
    private _modeController = new ModeRouterController(this);

    // Resource-mode sidebar entries (single Übersicht entry in V1). Built
    // lazily via getter so `translate()` runs after translations load,
    // `prefix: html``` matches the ToDo pattern (icon wired in todo_sidebar.css
    // via the `a[href="..."] :before` selector), and the path is SLASH-LESS to
    // satisfy the active-class check (`route.path === currentPath`, where
    // currentPath is `_router.link().substring(1)`).
    private get _resourceNavItems(): NavigationItem[] {
        return [
            {
                label: translate('wissen.nav.resource-overview'),
                prefix: html``,
                route: {path: 'resource-management'}
            }
        ];
    }

    /**
     * "My Organizer" mode's sidebar submenu (Anke, 2026-09-06: "es wird
     * unter 'my organizer' 2 Seiten geben: 'Meine Boxen'... und 'Mein
     * Kalender'... bitte mache den Aufbau exakt wie in der Todo-Sidebar") —
     * same static, per-mode nav-items array as `_resourceNavItems` above
     * (Todo's own sidebar list is backend-injected via `this.navigation`,
     * not a component-local array — this mirrors Resource's variant of the
     * same "one entry per mode page" pattern instead, since My Organizer has
     * no backend-injected nav config either).
     */
    private get _personalOrganizerNavItems(): NavigationItem[] {
        // Order and paths live in nav-item-filters.ts (unit tested there).
        return PERSONAL_ORGANIZER_NAV.map(({labelKey, path}) => ({
            label: translate(labelKey),
            prefix: html``,
            route: {path}
        }));
    }

    private _router: Router = (() => {
        const router = new Router(this, routes);
        window.addEventListener(`va.router.goto`, (evt: CustomEvent<{
            value: string
        }>) => router.goto(evt.detail.value).then());
        return router;
    })()

    constructor() {
        super();
        new StateController(this, appState);
        appState.router = this._router;
    }


    public async connectedCallback() {
        super.connectedCallback();

        // Block any logged-in-aware bootstrapping until the boot-time
        // whoami hydration has settled. Otherwise PersistenceConfig and the
        // initial preload check `appState.isUserLoggedIn` against a stale
        // null state and skip preload for an actually authenticated user.
        await awaitAuthHydration();

        // Clear a stale `ha_user` token (non-null user but expired `validUntil`).
        // The auth-gate in render() then flips reactively to the login form.
        if (appState.user !== null && !appState.isUserLoggedIn) {
            appState.clearUser();
        }

        // Capture the intended deep-link before the gate renders the login form,
        // so the post-login redirect (ha-form-login) returns the user to context.
        // Never captured in render() — that would loop via StateController.
        if (!appState.isUserLoggedIn
            && window.location.pathname !== '/auth/login'
            && window.location.pathname !== '/auth/logout') {
            appState.redirectAfterLogin = window.location.pathname;
        }

        PersistenceConfig.initialize({ isLoggedIn: () => appState.isUserLoggedIn });
        if (appState.isUserLoggedIn) {
            container.resolve(PersistencePreloader).preloadAll();
            void this._userDisplayNameController.refresh();
        }
        appState.listen$('user').pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            // Not filtered by login state: a logout/session expiry must reach
            // `UserDisplayNameController.refresh` too, so it clears the previous user's
            // name instead of leaving it up for the next person.
            if (appState.isUserLoggedIn) {
                container.resolve(PersistencePreloader).preloadAll();
            }
            void this._userDisplayNameController.refresh();
        });
        // The app-wide timer watcher (feature plan 0073): the timer's tone in every view, while logged in. Bound
        // before the translations load — the shell renders only after them, so the watcher has silently ended
        // long-expired runs before the first timer element mounts.
        bindTimerWatchToSession(appState, this._disconnected$);

        // Resolve the tenant-derived i18n override layer from the serve-time injected
        // global. sanitizeTenant fails closed (null) for absent/unsafe values, so the
        // fallback branch still loads the base `de` layer — both branches await use(...)
        // before flipping _translationsReady. The sanitized value is used only here and
        // is never written back into the global (which stays the raw injected tenant).
        const tenant = sanitizeTenant(window.__APP_CONTEXT__);
        if (tenant === null) {
            await use(`de`);
        } else {
            await use(`de.${tenant}`);
        }
        this._translationsReady = true;

        fromEvent<MouseEvent>(this, `mousemove`).pipe(
            takeUntil(this._disconnected$),
            map(value => ({x: value.clientX, y: value.clientY}))
        ).subscribe(this._onMouseMove.bind(this));

        // Listen for window resize to update responsive sidebar state
        fromEvent(window, 'resize').pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => {
            // Only trigger re-render if user hasn't set a preference
            if (appState.sidebarNavigationState === null) {
                this.requestUpdate();
            }
        });

        this._initDesktopNotifications();
    }

    /**
     * Keeps the browser tab title at "<app name> – <view>" (feature plan 0067, page-title.helper.ts).
     * Every route change, login/logout (StateController) and language change (the
     * TranslationController of AbstractLitElement, e.g. after saving in the translation editor)
     * re-renders this element, so updated() is the one place to set it. `location.pathname`, not
     * `this._router.link()`: link() reads "undefined" before the first goto and "/" on the
     * fallback route. That assumes the URL is pushed before the router renders, as
     * NavigationHelper.goto does; a bare `router.goto(x)` without pushState would leave the title
     * one view behind (today only used towards the login or `/`, which redirects with pushState).
     */
    protected updated(): void {
        if (!this._translationsReady) {
            return;
        }
        const title = pageTitleFor({
            isLoggedIn: appState.isUserLoggedIn,
            pathname: window.location.pathname,
            routes,
            translate: get,
        });
        if (document.title !== title) {
            document.title = title;
        }
    }

    protected render(): HTMLTemplateResult {
        if (!this._translationsReady) {
            return html``;
        }
        // Top-level auth-gate: the router outlet (and the whole app shell) is only
        // mounted when authenticated. Otherwise the login view renders in its place.
        // StateController re-renders this component on any appState.user change, so
        // the switch between login form and app shell is fully reactive.
        return when(
            appState.isUserLoggedIn,
            () => this._renderAuthenticatedShell(),
            () => html`<ha-login-view></ha-login-view>`
        );
    }

    /** Authenticated app shell: sidebar + router outlet. Only rendered when logged in. */
    private _renderAuthenticatedShell(): HTMLTemplateResult {
        const currentPath = this._router.link().substring(1);
        // Nav source per mode: ToDo uses the backend-injected `navigation`,
        // Resource uses `_resourceNavItems`, My Organizer `_personalOrganizerNavItems`.
        // Wissen, Linkview and Gimmicks have none — Wissen's former single
        // "Übersicht" entry was removed 2026-08-17 (no function once
        // <knowledge-sidebar-list> below makes the list/create area permanently
        // visible instead of something you navigate TO).
        const modeNavItems = appState.currentMode === 'todo'
            ? this.navigation
            : appState.currentMode === 'resource'
                ? this._resourceNavItems
                : appState.currentMode === 'personal-organizer'
                    ? this._personalOrganizerNavItems
                    : [];
        // Filtering logic itself lives in nav-item-filters.ts (pure, unit tested there
        // directly — see that module's docblock for why it's split out).
        const navItems = buildNavItems(
            FEATURE_FLAGS.myView,
            modeNavItems,
        );
        // `<knowledge-sidebar-list>` (below) needs the ROUTED object's uid to mark
        // the right row `.selected` — parsed straight from the path rather than
        // threaded through as a route param, since this component isn't the
        // route's rendered element (`knowledge-overview-view` is). Matches the
        // `/knowledge/:sid/:slug?` pattern from routes.ts; `undefined` on the bare
        // `/knowledge` list.
        const knowledgeSid = parseKnowledgeSid(appState.currentMode, currentPath);
        // Same reasoning as knowledgeSid above, for <linkview-sidebar-list>
        // (Anke, 2026-09-04) — `/linkview/:sid/:slug?` from routes.ts.
        const linkviewSid = parseLinkviewSid(appState.currentMode, currentPath);
        const sidebarStyle = computeSidebarStyle(appState.sidebarWidth);
        return html`
            <sidebar ${ref(this._sidebarRef)} class="container ${classMap({collapsed: this._isSidebarCollapsed})}" style=${sidebarStyle}>
                <div class="sidebar__navigation">
                    <main class="sidebar__navigation__main">
                        <ha-button-with-submenu
                                .items=${getSubMenuItems()}
                                button-title=${translate('mainToolbar.settings')}
                                class="sidebar-settings-link"
                                submenu-layout="list"
                        ></ha-button-with-submenu>
                        ${this._userDisplayName
                            ? html`<div class="sidebar-user-name" title=${this._userDisplayName}>${this._userDisplayName}</div>`
                            : html``}
                        <ha-mode-switch .modeController=${this._modeController}
                                        ?collapsed=${this._isSidebarCollapsed}
                        ></ha-mode-switch>
                        <nav role="navigation">
                            ${repeat(navItems, navigationItem => navigationItem.route?.path ?? navigationItem.label, navigationItem => html`
                                ${when(!!navigationItem.route,
                                        () => {
                                            // Active when the current URL exactly matches the nav path
                                            // OR is a child route (e.g. /knowledge/<sid>/<slug>) — see
                                            // isNavItemActive's own doc comment for why a plain prefix
                                            // check alone isn't enough once sibling pages nest textually
                                            // (My Organizer's "Meine Boxen"/"Mein Kalender").
                                            const isActive = isNavItemActive(navigationItem, navItems, currentPath);
                                            const cssClasses = {active: isActive}

                                            return html`
                                                <a class=${classMap(cssClasses)}
                                                   href=${navigationItem.route!.path}
                                                   title=${navigationItem.label ?? ''}
                                                >${this._renderNavigationItem(navigationItem)}</a>
                                            `
                                        },
                                        () => this._renderNavigationItem(navigationItem)
                                )}
                            `)}
                        </nav>
                        ${when(appState.currentMode === 'wissen', () => html`
                            <knowledge-sidebar-list
                                    .selectedSid=${knowledgeSid ?? null}
                            ></knowledge-sidebar-list>
                        `)}
                        ${when(appState.currentMode === 'linkview', () => html`
                            <linkview-sidebar-list
                                    .selectedSid=${linkviewSid ?? null}
                            ></linkview-sidebar-list>
                        `)}
                    </main>
                    <header class="sidebar__navigation__header">
                        <lottie-player
                                @load=${this._onLoadAnimation}
                                ${ref(this._lottiePlayerRef)}
                                src=${JSON.stringify(data)}
                                autoplay
                                loop
                                mode="normal"
                        ></lottie-player>
                    </header>
                </div>
                <div class="sidebar-resize-handle"
                     @mousedown=${this._onSidebarResizeStart}
                     @dblclick=${this._onSidebarResizeDoubleClick}
                ></div>
            </sidebar>
            <div class="right-section">
                <main class="container content">${this._router.outlet()}</main>
            </div>
        `;
    }

    private _initDesktopNotifications(): void {
        if (!("Notification" in window)) {
            console.warn(`Browser unterstützt keine Desktop-Benachrichtigungen.`);
            return;
        }

        // Nach Erlaubnis fragen
        if (Notification.permission !== "denied") {
            Notification.requestPermission().then();
        }
    }

    private _onLoadAnimation(): void {
        lottieInteractivityCreate({
            player: this._lottiePlayerRef.value,
            mode: 'cursor',
            actions: [
                {
                    type: "hover",
                    forceFlag: false
                }
            ],
        });
    }

    /**
     * Remembers the pointer position for the hover popover. Deliberately NOT written onto <html> here:
     * a custom property on the root element restyles the whole document on every mouse move (see
     * PointerPositionHelper); `UiHelper.popover` writes it there only when a popover opens.
     */
    private _onMouseMove(position: { x: number, y: number }): void {
        PointerPositionHelper.update(position.x, position.y);
    }

    /**
     * Sidebar resize drag (operator request 2026-08-17: a full-height handle
     * on the sidebar's right edge, todo_sidebar.css `.sidebar-resize-handle`,
     * replacing the earlier native corner-resize approach).
     *
     * Deliberately unclamped WHILE DRAGGING: `startWidth + deltaX` is written
     * straight to `style.width`, and the sidebar's own `min-width`/`max-width`
     * (todo_sidebar.css) are what the BROWSER actually renders it at — CSS stays
     * the single source of truth for the bounds instead of duplicating them
     * into JS math. On drop, the browser-CLAMPED width (not the raw,
     * possibly out-of-bounds value above) is what gets persisted.
     */
    private _onSidebarResizeStart(event: MouseEvent): void {
        const sidebarEl = this._sidebarRef.value;
        if (!sidebarEl) {
            return;
        }
        event.preventDefault();

        const handleEl = event.currentTarget as HTMLElement;
        handleEl.classList.add('is-dragging');

        const startX = event.clientX;
        const startWidth = sidebarEl.getBoundingClientRect().width;

        // `take(1)`: this drag's mouseup fires ONCE. `share()`: both usages
        // below (the mousemove cutoff via `merge` and the explicit
        // `.subscribe()` for drag-end cleanup) multicast off the SAME
        // underlying `fromEvent` subscription — without it, each subscriber
        // registers its OWN native `window` "mouseup" listener (review 01,
        // MINOR, Linse A: two listeners per drag instead of one).
        const mouseUp$ = fromEvent<MouseEvent>(window, `mouseup`).pipe(take(1), share());

        // `auditTime(16)` (~1 frame @ 60fps): review 03, MINOR (Linse A) —
        // writing `style.width` on every single native mousemove forces a
        // reflow of the whole right-hand section (the sidebar is a container-
        // query root) on every pixel of pointer movement; batching to one
        // trailing write per frame keeps the drag visually smooth without
        // introducing a fixed frame-based loop of its own.
        fromEvent<MouseEvent>(window, `mousemove`).pipe(
            takeUntil(merge(mouseUp$, this._disconnected$)),
            auditTime(16),
        ).subscribe(moveEvent => {
            sidebarEl.style.width = `${computeDraggedWidth(startWidth, startX, moveEvent.clientX)}px`;
        });

        mouseUp$.pipe(takeUntil(this._disconnected$)).subscribe(() => {
            handleEl.classList.remove('is-dragging');

            // Only persist if the width actually moved (review finding
            // 2026-08-28, Linse B/C: a plain click — not just the drag-start
            // half of a double-click — ran this full mousedown/mouseup cycle
            // too, unconditionally writing appState.sidebarWidth even with
            // zero drag delta. Harmless once a width was already persisted
            // (Object.is no-op), but on the very FIRST click/double-click of
            // a session (sidebarWidth still null, sidebar at its CSS
            // fit-content default) that pinned the then-current width as a
            // real state change + localStorage write, a moment before the
            // double-click handler's real target width overwrote it again.
            const currentWidth = sidebarEl.getBoundingClientRect().width;
            if (currentWidth === startWidth) {
                return;
            }
            // Persist so the width survives reload/re-navigation (review 01,
            // INFO -> "sollte auch gemerkt werden").
            appState.sidebarWidth = currentWidth;
        });
    }

    /**
     * Double-click on the resize handle toggles the sidebar between the CSS
     * minimum width and a second, "everything readable" width (operator
     * request 2026-08-28) — `--sidebar-min-width`
     * (theme-sidebar-default.css) and the new `--sidebar-readable-width`
     * (theme-default.css, currently 15rem, adjustable there without touching this
     * code). Both are resolved via `_resolveCssWidthPx` (measured through the
     * browser itself) rather than parsed as CSS strings in JS, so this stays
     * correct regardless of unit (px vs rem) — same "CSS is the single
     * source of truth for the bounds" principle as `_onSidebarResizeStart`
     * above.
     */
    private _onSidebarResizeDoubleClick(): void {
        const sidebarEl = this._sidebarRef.value;
        if (!sidebarEl) {
            return;
        }

        const currentWidth = sidebarEl.getBoundingClientRect().width;
        const minWidth = this._resolveCssWidthPx(sidebarEl, 'var(--sidebar-min-width)');
        const readableWidth = this._resolveCssWidthPx(sidebarEl, 'var(--sidebar-readable-width)');
        const targetWidth = computeToggledSidebarWidth(currentWidth, minWidth, readableWidth);

        sidebarEl.style.width = `${targetWidth}px`;
        appState.sidebarWidth = targetWidth;
    }

    /**
     * Resolves a CSS width expression (e.g. a `var(--x)` reference) to its
     * browser-computed pixel value: applies it as a momentary inline style
     * and reads `getBoundingClientRect()` back, then restores the element's
     * previous inline width — both happen synchronously before the next
     * paint, so there's no visible flicker. Avoids duplicating CSS-unit
     * conversion math in JS.
     */
    private _resolveCssWidthPx(el: HTMLElement, cssWidthExpression: string): number {
        const previousInlineWidth = el.style.width;
        el.style.width = cssWidthExpression;
        const px = el.getBoundingClientRect().width;
        el.style.width = previousInlineWidth;
        return px;
    }

    private _renderNavigationItem(navigationItem: NavigationItem): HTMLTemplateResult {
        return html`
            <ha-navigation-item .item=${navigationItem}
                                ?collapsed=${this._isSidebarCollapsed}
            ></ha-navigation-item>
        `
    }
}
